import { useCallback, ReactElement, useMemo, memo } from 'react';
import { Box, Slider } from '@mui/material';
import styles from '../Filter.module.scss';
import { BaseFilterProps } from './common';
import { FilterNumberRangeOptionDTO, FloatFilterDTO, NumberRangeDTO } from '../../../api/generated';
import { clipToRange, skipUndefined } from '../../../utils';
import { CommittingNumberInput } from '../CommittingNumberInput';

export interface NumberRangeFilterProps extends BaseFilterProps<FloatFilterDTO, FilterNumberRangeOptionDTO> {
  unitName?: string;
}

export const NumberRangeFilter = memo(function ({
  value,
  onValueChange,
  onCommit,
  filterOptions,
  unitName,
}: NumberRangeFilterProps): ReactElement {
  const isDisabled = useMemo(() => !filterOptions, [filterOptions]);
  const lowValue = useMemo(() => value?.range?.gte || filterOptions?.min || 0, [value, filterOptions]);
  const highValue = useMemo(() => value?.range?.lte || filterOptions?.max || 0, [value, filterOptions]);

  const updateRange = useCallback(
    function (low?: number, high?: number): void {
      let gte = skipUndefined(low, (v) => clipToRange(v, filterOptions?.min, filterOptions?.max));
      let lte = skipUndefined(high, (v) => clipToRange(v, filterOptions?.min, filterOptions?.max));
      gte = skipUndefined(gte, (v) => clipToRange(v, undefined, lte));
      lte = skipUndefined(lte, (v) => clipToRange(v, low, undefined));

      const range: NumberRangeDTO = {};
      if (gte !== filterOptions?.min) {
        range.gte = gte;
      }
      if (lte !== filterOptions?.max) {
        range.lte = lte;
      }
      onValueChange(Object.keys(range).length ? { range } : undefined);
    },
    [onValueChange, filterOptions]
  );

  const handleSliderChange = useCallback(
    function (_: any, newValue: number | number[]): void {
      if (Array.isArray(newValue)) {
        updateRange(newValue[0], newValue[1]);
      }
    },
    [updateRange]
  );

  const handleTextLowChange = useCallback(
    function (textFieldValue?: number): void {
      updateRange(textFieldValue, value?.range?.lte);
    },
    [value, updateRange]
  );

  const handleTextHighChange = useCallback(
    function (textFieldValue?: number): void {
      updateRange(value?.range?.gte, textFieldValue);
    },
    [value, updateRange]
  );

  return (
    <Box
      className={`${styles.flex_container} ${styles.flex_vertical} ${styles.flex_container_width} ${styles.centered}`}
    >
      <Box
        className={`${styles.flex_container_width} ${styles.centered}`}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '1rem',
          backgroundColor: '#f2f2f2',
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
        }}
      >
        <Slider
          value={[lowValue, highValue]}
          onChange={handleSliderChange}
          onChangeCommitted={onCommit}
          valueLabelDisplay='auto'
          valueLabelFormat={(v) => `${v}${unitName}`}
          min={filterOptions?.min || 0}
          max={filterOptions?.max || 0}
          disabled={isDisabled}
          disableSwap
        />
        {unitName && <span>{unitName}</span>}
      </Box>
      <Box
        className={`${styles.flex_container_width} ${styles.centered}`}
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem' }}
      >
        <CommittingNumberInput
          size='small'
          value={lowValue}
          onValueChange={handleTextLowChange}
          onCommit={onCommit}
          disabled={isDisabled}
        />
        <hr
          style={{
            borderTop: '1px solid darkgray',
            width: '1rem',
            height: '0px',
          }}
        />
        <CommittingNumberInput
          size='small'
          value={highValue}
          onValueChange={handleTextHighChange}
          onCommit={onCommit}
          disabled={isDisabled}
        />
      </Box>
    </Box>
  );
});
